import { isEqual } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Row } from 'react-table'
import { Moment } from 'moment'
import useFetchRefactored from 'civic-champs-shared/api/hooks/useFetchRefactored'
import { useCurrentOrg } from 'auth/hooks'
import { Volunteer } from 'champion/utils/interface'
import useLazyRef from 'utils/useLazyRef'
import {
  CHAMPIONS_SET_SELECTED_ROW_IDS,
  ChampionsTableDispatch,
} from 'champion/hooks/championsServerSideSelectionConstants'
import { ChampionsServerSideSelectionPluginApi } from 'champion/hooks/useChampionsServerSideSelectionPlugin'
import { formatDate } from 'volunteering/volunteers/hooks/utils'
import useGetTableSortBy from 'core/table/table-hooks/useGetTableSortBy'
import useSetTableSortBy from 'core/table/table-hooks/useSetTableSortBy'
import { sortByToSortOrder, sortOrderToSortBy } from 'core/table/interfaces/TablePersistence'
import { ChampionsPagedSummaryResponse, championsTableName } from 'champion/championsTableConfig'

const EMPTY_PAGED_SUMMARY: ChampionsPagedSummaryResponse = {
  count: 0,
  totalVolunteerHours: 0,
  totalDonationsCents: 0,
}

const DEFAULT_PAGE_SIZE = 25
/** Chunk size for select-all fetches; same as useVolunteersCollection loadInChunks. */
const FETCH_ALL_PAGE_SIZE = 5000

export type ChampionsDateRange = {
  startDate: Moment | null
  endDate: Moment | null
} | null

export type ChampionsTableSort = { id: string; desc: boolean }[]

const DEFAULT_SORT: ChampionsTableSort = [{ id: 'id', desc: false }]

const SORT_COLUMN_TO_API: Record<string, string> = {
  id: 'id',
  personId: 'id',
  firstName: 'givenName',
  lastName: 'familyName',
  email: 'email',
  startedAt: 'startedAt',
  latestActivity: 'latestActivity',
  hours: 'totalVolunteerHours',
  donations: 'totalDonations',
  status: 'status',
}

const buildFilterParams = ({
  search,
  dateRange,
  includeInactive,
  columnFilters,
}: {
  search: string
  dateRange: ChampionsDateRange
  includeInactive: boolean
  columnFilters?: string
}) => ({
  showInactive: includeInactive ? 'true' : 'false',
  ...(search ? { search } : {}),
  ...(dateRange?.startDate ? { startDate: formatDate(dateRange.startDate) } : {}),
  ...(dateRange?.endDate ? { endDate: formatDate(dateRange.endDate) } : {}),
  ...(columnFilters ? { filters: columnFilters } : {}),
})

export const useFetchChampions = (dateRange: ChampionsDateRange, includeInactive: boolean, columnFilters?: string) => {
  const organization = useCurrentOrg()
  const [volunteers, setVolunteers] = useState<Volunteer[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [pagedSummary, setPagedSummary] = useState<ChampionsPagedSummaryResponse>(EMPTY_PAGED_SUMMARY)
  const [loading, setLoading] = useState(true)
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<ChampionsTableSort>(DEFAULT_SORT)
  const [sortPreferencesReady, setSortPreferencesReady] = useState(false)
  const [request] = useFetchRefactored()
  const [fetchTableSortBy, { result: savedSortBy, loading: sortLoading, called: sortCalled }] = useGetTableSortBy()
  const [setTableSortBy] = useSetTableSortBy()
  const skipSortSave = useRef(true)
  const sortByRef = useRef<ChampionsTableSort>(DEFAULT_SORT)
  const sortInitStarted = useRef(false)
  const searchRef = useRef('')
  const lastFilterKeyRef = useRef<string | null>(null)
  const fetchAllMatchingInFlightRef = useRef<Promise<Volunteer[]> | null>(null)

  const filterKey = useMemo(
    () => JSON.stringify(buildFilterParams({ search, dateRange, includeInactive, columnFilters })),
    [search, dateRange, includeInactive, columnFilters],
  )

  useEffect(() => {
    if (sortInitStarted.current) {
      return
    }
    sortInitStarted.current = true
    fetchTableSortBy(championsTableName).catch(() => undefined)
  }, [fetchTableSortBy])

  useEffect(() => {
    if (!sortCalled || sortLoading || sortPreferencesReady) {
      return
    }

    const persisted = sortOrderToSortBy(savedSortBy?.sortOrder || '')
    if (persisted.length) {
      sortByRef.current = persisted
      setSortBy(persisted)
    }

    skipSortSave.current = false
    setSortPreferencesReady(true)
  }, [savedSortBy, sortCalled, sortLoading, sortPreferencesReady])

  const refetch = useCallback(
    async ({ includeCount }: { includeCount?: boolean } = {}) => {
      setLoading(true)
      const filterParams = buildFilterParams({ search, dateRange, includeInactive, columnFilters })
      const shouldFetchCount = includeCount ?? lastFilterKeyRef.current !== filterKey

      if (shouldFetchCount) {
        lastFilterKeyRef.current = filterKey
      }

      const [{ id, desc }] = sortBy
      const sortParams = {
        sortBy: SORT_COLUMN_TO_API[id] || 'id',
        sortDir: desc ? 'desc' : 'asc',
      }

      const pageRequest = request({
        url: `/organizations/${organization.id}/volunteers-paged`,
        config: {
          queryStringParameters: {
            ...filterParams,
            ...sortParams,
            limit: pageSize,
            offset: pageIndex * pageSize,
          },
        },
      }) as Promise<Volunteer[]>

      try {
        if (shouldFetchCount) {
          const [summary, page] = await Promise.all([
            request({
              url: `/organizations/${organization.id}/volunteers-paged-count`,
              config: { queryStringParameters: filterParams },
            }) as Promise<ChampionsPagedSummaryResponse>,
            pageRequest,
          ])
          setTotalCount(summary.count)
          setPagedSummary(summary)
          setVolunteers(page)
        } else {
          setVolunteers(await pageRequest)
        }
      } catch {
        if (shouldFetchCount) {
          setTotalCount(0)
          setPagedSummary(EMPTY_PAGED_SUMMARY)
        }
        setVolunteers([])
      } finally {
        setLoading(false)
      }
    },
    [
      columnFilters,
      dateRange,
      filterKey,
      includeInactive,
      organization.id,
      pageIndex,
      pageSize,
      request,
      search,
      sortBy,
    ],
  )

  const refetchWithCount = useCallback(() => refetch({ includeCount: true }), [refetch])

  const fetchAllMatchingVolunteers = useCallback(async (): Promise<Volunteer[]> => {
    if (fetchAllMatchingInFlightRef.current) {
      return fetchAllMatchingInFlightRef.current
    }

    const fetchPromise = (async () => {
      const filterParams = buildFilterParams({ search, dateRange, includeInactive, columnFilters })
      const [{ id, desc }] = sortBy
      const sortParams = {
        sortBy: SORT_COLUMN_TO_API[id] || 'id',
        sortDir: desc ? 'desc' : 'asc',
      }

      const all: Volunteer[] = []
      let offset = 0

      while (true) {
        const page = (await request({
          url: `/organizations/${organization.id}/volunteers-paged`,
          config: {
            queryStringParameters: {
              ...filterParams,
              ...sortParams,
              limit: FETCH_ALL_PAGE_SIZE,
              offset,
            },
          },
        })) as Volunteer[]

        all.push(...page)
        if (page.length < FETCH_ALL_PAGE_SIZE) {
          break
        }
        offset += FETCH_ALL_PAGE_SIZE
      }

      return all
    })()

    fetchAllMatchingInFlightRef.current = fetchPromise

    try {
      return await fetchPromise
    } finally {
      if (fetchAllMatchingInFlightRef.current === fetchPromise) {
        fetchAllMatchingInFlightRef.current = null
      }
    }
  }, [columnFilters, dateRange, includeInactive, organization.id, request, search, sortBy])

  useEffect(() => {
    if (!sortPreferencesReady) {
      return
    }
    refetch()
  }, [refetch, sortPreferencesReady])

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1)
    if (pageIndex > maxPage) {
      setPageIndex(maxPage)
    }
  }, [totalCount, pageSize, pageIndex])

  useEffect(() => {
    setPageIndex(0)
  }, [dateRange, includeInactive, columnFilters])

  const onSearchChange = useCallback((query: string) => {
    if (searchRef.current === query) {
      return
    }
    searchRef.current = query
    setSearch(query)
    setPageIndex(0)
  }, [])

  const onSortChange = useCallback(
    (nextSortBy: ChampionsTableSort) => {
      const normalized = nextSortBy.length ? nextSortBy : DEFAULT_SORT
      if (isEqual(sortByRef.current, normalized)) {
        return
      }
      sortByRef.current = normalized
      setSortBy(normalized)
      setPageIndex(0)

      if (!skipSortSave.current) {
        const sortOrder = sortByToSortOrder(normalized)
        if (sortOrder) {
          setTableSortBy(championsTableName, sortOrder)
        }
      }
    },
    [setTableSortBy],
  )

  const handlePageSizeChange = useCallback((nextPageSize: number) => {
    setPageSize(nextPageSize)
    setPageIndex(0)
  }, [])

  const listLoading = loading || !sortPreferencesReady
  const [selectedRowIds, setSelectedRowIds] = useState<Record<string, boolean>>({})
  const [selectingAll, setSelectingAll] = useState(false)
  const selectAllInFlightRef = useRef(false)
  const volunteerByPersonIdRef = useLazyRef(() => new Map<number, Volunteer>())
  const volunteerByPersonId = volunteerByPersonIdRef.current as unknown as Map<number, Volunteer>
  const selectionPluginApiRef = useRef<ChampionsServerSideSelectionPluginApi>({
    onSelectedRowIdsChange: () => undefined,
    syncVolunteersFromSelection: () => undefined,
  })

  useEffect(() => {
    volunteers.forEach(volunteer => volunteerByPersonId.set(volunteer.user.id, volunteer))
  }, [volunteers, volunteerByPersonId])

  const syncVolunteersFromSelection = useCallback(
    (ids: Record<string, boolean>) => {
      volunteers.forEach(volunteer => {
        if (ids[String(volunteer.user.id)]) {
          volunteerByPersonId.set(volunteer.user.id, volunteer)
        }
      })
    },
    [volunteers, volunteerByPersonId],
  )

  selectionPluginApiRef.current = {
    onSelectedRowIdsChange: setSelectedRowIds,
    syncVolunteersFromSelection,
  }

  const selectedCount = useMemo(
    () => Object.keys(selectedRowIds).filter(id => selectedRowIds[id]).length,
    [selectedRowIds],
  )

  const clearSelection = useCallback((dispatch: ChampionsTableDispatch) => {
    setSelectedRowIds({})
    dispatch({ type: CHAMPIONS_SET_SELECTED_ROW_IDS, selectedRowIds: {} })
  }, [])

  const selectAllMatching = useCallback(
    async (dispatch: ChampionsTableDispatch) => {
      if (totalCount === 0 || listLoading || selectAllInFlightRef.current) {
        return
      }

      selectAllInFlightRef.current = true
      setSelectingAll(true)
      try {
        const all = await fetchAllMatchingVolunteers()
        all.forEach(volunteer => volunteerByPersonId.set(volunteer.user.id, volunteer))
        const ids = all.reduce<Record<string, boolean>>((acc, volunteer) => {
          acc[String(volunteer.user.id)] = true
          return acc
        }, {})
        setSelectedRowIds(ids)
        dispatch({ type: CHAMPIONS_SET_SELECTED_ROW_IDS, selectedRowIds: ids })
      } finally {
        selectAllInFlightRef.current = false
        setSelectingAll(false)
      }
    },
    [fetchAllMatchingVolunteers, listLoading, totalCount, volunteerByPersonId],
  )

  const getSelectedRows = useCallback((): Row<Volunteer>[] => {
    return Object.keys(selectedRowIds)
      .filter(id => selectedRowIds[id])
      .map(id => volunteerByPersonId.get(Number(id)))
      .filter((volunteer): volunteer is Volunteer => Boolean(volunteer))
      .map(original => ({ original } as Row<Volunteer>))
  }, [selectedRowIds, volunteerByPersonId])

  return useMemo(
    () => ({
      volunteers,
      totalCount,
      pagedSummary,
      loading: listLoading,
      pageIndex,
      setPageIndex,
      pageSize,
      setPageSize: handlePageSizeChange,
      sortBy,
      sortPreferencesReady,
      onSearchChange,
      onSortChange,
      refetch: refetchWithCount,
      filterKey,
      selectedRowIds,
      selectedCount,
      selectingAll,
      selectionPluginApiRef,
      clearSelection,
      selectAllMatching,
      getSelectedRows,
    }),
    [
      volunteers,
      totalCount,
      pagedSummary,
      listLoading,
      pageIndex,
      pageSize,
      handlePageSizeChange,
      sortBy,
      sortPreferencesReady,
      onSearchChange,
      onSortChange,
      refetchWithCount,
      filterKey,
      selectedRowIds,
      selectedCount,
      selectingAll,
      clearSelection,
      selectAllMatching,
      getSelectedRows,
    ],
  )
}
